import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Hero from '../components/Hero';
import Features from '../components/Features';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import { categories, getFeaturedProducts, getSaleProducts } from '../data/products';

function Home() {
  const featuredProducts = getFeaturedProducts();
  const saleProducts = getSaleProducts();

  return (
    <Box>
      {/* Hero Section */}
      <Hero />

      {/* Features Bar */}
      <Features />

      {/* Categories Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.dark',
                letterSpacing: 2,
                fontWeight: 600,
              }}
            >
              Find Your Perfect Pillow
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, mb: 2 }}>
              Shop by Category
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Every sleeper is unique. Explore our curated collection of pillows 
              designed for every sleep style and preference.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {categories.slice(0, 6).map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <CategoryCard category={category} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Best Sellers Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              mb: 4,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.dark',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Customer Favorites
              </Typography>
              <Typography variant="h2" sx={{ mt: 1 }}>
                Best Sellers
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/products?filter=bestseller"
              endIcon={<ArrowForwardIcon />}
              sx={{ color: 'primary.main' }}
            >
              View All
            </Button>
          </Box>

          <Grid container spacing={3}>
            {featuredProducts.slice(0, 4).map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Sleepout Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Why Choose Sleepout
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3, color: 'white' }}>
                Designed for Your Best Sleep
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.8 }}
              >
                At Sleepout, we believe everyone deserves a great night's sleep. 
                Our pillows are meticulously crafted using premium materials and 
                backed by sleep science to ensure optimal comfort and support.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  'Premium quality materials sourced responsibly',
                  'Designed with input from sleep specialists',
                  'Rigorously tested for durability and comfort',
                  'Backed by our 100-night satisfaction guarantee',
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'secondary.main',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                component={RouterLink}
                to="/about"
                variant="outlined"
                size="large"
                sx={{
                  mt: 4,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Learn More About Us
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    bgcolor: 'secondary.main',
                    borderRadius: '50%',
                    opacity: 0.3,
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80"
                  alt="Comfortable bedroom"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sale Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              mb: 4,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: 'error.main',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Limited Time Offers
              </Typography>
              <Typography variant="h2" sx={{ mt: 1 }}>
                On Sale Now
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/products?filter=sale"
              endIcon={<ArrowForwardIcon />}
              color="error"
            >
              Shop All Sale
            </Button>
          </Box>

          <Grid container spacing={3}>
            {saleProducts.slice(0, 4).map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Newsletter Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Sleep Tips & Exclusive Offers
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}
            >
              Join our newsletter for expert sleep advice, new product launches, 
              and exclusive discounts.
            </Typography>
            <Box
              component="form"
              sx={{
                display: 'flex',
                gap: 2,
                maxWidth: 500,
                mx: 'auto',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box
                component="input"
                type="email"
                placeholder="Enter your email"
                sx={{
                  flex: 1,
                  px: 3,
                  py: 1.5,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  fontSize: '1rem',
                  outline: 'none',
                  '&:focus': {
                    borderColor: 'primary.main',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ px: 4 }}
              >
                Subscribe
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
